// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import {SpoilerOverlay} from '@app/features/app/components/shared/SpoilerOverlay';
import {AttachmentMosaic} from '@app/features/channel/components/embeds/attachments/AttachmentMosaic';
import styles from '@app/features/channel/components/embeds/Embed.module.css';
import {hasRichEmbedContent} from '@app/features/channel/components/embeds/EmbedRenderUtils';
import EmbedAudio from '@app/features/channel/components/embeds/media/EmbedAudio';
import {EmbedGif, EmbedGifv} from '@app/features/channel/components/embeds/media/EmbedGifv';
import {EmbedImage} from '@app/features/channel/components/embeds/media/EmbedImage';
import EmbedVideo from '@app/features/channel/components/embeds/media/EmbedVideo';
import {EmbedYouTube} from '@app/features/channel/components/embeds/media/EmbedYouTube';
import Channels from '@app/features/channel/state/Channels';
import Guilds from '@app/features/guild/state/Guilds';
import * as MessageCommands from '@app/features/messaging/commands/MessageCommands';
import {SafeMarkdown} from '@app/features/messaging/components/markdown';
import {MarkdownContext} from '@app/features/messaging/components/markdown/renderers/RendererTypes';
import type {Message} from '@app/features/messaging/models/Message';
import {openExternalUrlWithWarning} from '@app/features/messaging/utils/ExternalLinkUtils';
import {getEmbedMediaDimensions} from '@app/features/messaging/utils/MediaDimensionConfig';
import {buildMediaProxyURL} from '@app/features/messaging/utils/MediaProxyUtils';
import {canonicalizeMediaUrl, extractSpoileredUrls, useSpoilerState} from '@app/features/messaging/utils/SpoilerUtils';
import Permission from '@app/features/permissions/state/Permission';
import {Logger} from '@app/features/platform/utils/AppLogger';
import markupStyles from '@app/features/theme/styles/Markup.module.css';
import messageStyles from '@app/features/theme/styles/Message.module.css';
import * as ColorUtils from '@app/features/theme/utils/ColorUtils';
import TrustedDomain from '@app/features/trusted_domain/state/TrustedDomain';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {createCalculator} from '@app/features/ui/utils/DimensionUtils';
import * as DateUtils from '@app/features/user/utils/dateFormatting';
import {MessageAttachmentFlags, MessageEmbedTypes, Permissions} from '@app/vendor/constants/ChannelConstants';
import {GuildOperations} from '@app/vendor/constants/GuildConstants';
import type {
	EmbedAuthor,
	EmbedField,
	EmbedFooter,
	EmbedMedia,
	MessageEmbed,
} from '@app/vendor/schema/domains/message/EmbedSchemas';
import type {MessageAttachment} from '@app/vendor/schema/domains/message/MessageResponseSchemas';
import {Trans, useLingui} from '@lingui/react/macro';
import {XIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {type FC, useCallback, useMemo} from 'react';

const logger = new Logger('Embed');
const THUMBNAIL_SIZE = 80;
const MAX_GALLERY_MEDIA = 10;
const EMBED_PADDING_X = 12;
const EMBED_LEFT_BORDER_WIDTH = 4;
const EMBED_RIGHT_BORDER_WIDTH = 1;

const EMBED_MEDIA_CHROME_WIDTH = EMBED_PADDING_X * 2 + EMBED_LEFT_BORDER_WIDTH + EMBED_RIGHT_BORDER_WIDTH;
interface EmbedProps {
	embed: MessageEmbed;
	message: Message;
	embedIndex?: number;
	onDelete?: (bypassConfirm?: boolean) => void;
	contextualEmbeds?: ReadonlyArray<MessageEmbed>;
	isPreview?: boolean;
}
interface LinkComponentProps {
	url: string;
	children: React.ReactNode;
	className?: string;
}
interface MediaDimensions {
	width: number;
	height: number;
}
const thumbnailCalculator = createCalculator({
	maxWidth: THUMBNAIL_SIZE,
	maxHeight: THUMBNAIL_SIZE,
	forceScale: true,
});
const isValidMedia = (media?: Partial<EmbedMedia>): media is Required<EmbedMedia> => {
	return !!(
		media &&
		typeof media.proxy_url === 'string' &&
		typeof media.url === 'string' &&
		typeof media.width === 'number' &&
		typeof media.height === 'number'
	);
};
const calculateMediaDimensions = (media: Required<EmbedMedia>): MediaDimensions => {
	const embedDimensions = getEmbedMediaDimensions();
	const mediaCalculator = createCalculator({
		maxWidth: embedDimensions.maxWidth,
		maxHeight: embedDimensions.maxHeight,
	});
	const {dimensions} = mediaCalculator.calculate({
		width: media.width,
		height: media.height,
	});
	return dimensions;
};
const getOptimizedMediaURL = (
	proxyURL: string,
	width: number,
	height: number,
	includeFormat = true,
	animated?: boolean,
): string => {
	const targetWidth = Math.round(width * 2);
	const targetHeight = Math.round(height * 2);
	return buildMediaProxyURL(proxyURL, {
		format: includeFormat ? 'webp' : undefined,
		width: targetWidth,
		height: targetHeight,
		animated,
	});
};
const SuppressEmbedsConfirmModal: FC<{message: Message}> = ({message}) => {
	const {t} = useLingui();
	return (
		<ConfirmModal
			title={t`Suppress Embeds`}
			description={
				<Trans>
					Are you sure you want to suppress all link embeds on this message? This action will hide all embeds from this
					message.
				</Trans>
			}
			primaryText={t`Suppress Embeds`}
			primaryVariant="danger-primary"
			onPrimary={async () => {
				await MessageCommands.toggleSuppressEmbeds(message.channelId, message.id, message.flags);
			}}
		/>
	);
};
const shouldRenderAsInlineThumbnail = (media?: EmbedMedia): boolean => {
	if (!isValidMedia(media)) return false;
	const {dimensions: thumbnailDimensions} = thumbnailCalculator.calculate({
		width: media.width,
		height: media.height,
	});
	const thumbnailWidth = thumbnailDimensions.width;
	const {width: normalWidth} = calculateMediaDimensions(media);
	return normalWidth < 300 && thumbnailWidth >= 40;
};
const isMediaNSFW = (media?: EmbedMedia): boolean => {
	if (!media) return false;
	return Boolean(media.nsfw || ((media.flags ?? 0) & MessageAttachmentFlags.CONTAINS_EXPLICIT_MEDIA) !== 0);
};
const normalizeUrl = (url?: string): string | null => {
	if (!url) return null;
	try {
		return new URL(url).href.replace(/\/$/, '');
	} catch {
		return null;
	}
};
const deriveFilenameFromUrl = (url: string): string => {
	try {
		const parsed = new URL(url);
		const filename = parsed.pathname.split('/').pop()?.trim();
		return filename && filename.length > 0 ? filename : 'embed-media';
	} catch {
		return 'embed-media';
	}
};
const buildGalleryAttachments = (
	images: Array<Required<EmbedMedia>>,
	embed: MessageEmbed,
	embedIndex?: number,
): Array<MessageAttachment> => {
	return images.map((media, index) => ({
		id: `${embed.id ?? embedIndex ?? 'embed'}-gallery-${index}`,
		filename: deriveFilenameFromUrl(media.url),
		title: embed.title ?? undefined,
		description: media.description ?? undefined,
		content_type: media.content_type ?? undefined,
		size: 0,
		url: media.url,
		proxy_url: media.proxy_url ?? media.url,
		width: media.width ?? undefined,
		height: media.height ?? undefined,
		placeholder: media.placeholder ?? undefined,
		placeholder_version: undefined,
		flags: media.flags,
		duration: media.duration,
		waveform: undefined,
		content_hash: media.content_hash ?? undefined,
		nsfw: media.nsfw,
	}));
};
const collectGalleryImages = ({
	embed,
	embedIndex,
	embedList,
}: {
	embed: MessageEmbed;
	embedIndex?: number;
	embedList: ReadonlyArray<MessageEmbed>;
}): Array<Required<EmbedMedia>> => {
	const normalizedUrl = normalizeUrl(embed.url);
	if (embedIndex === undefined || !normalizedUrl) return [];
	const images: Array<Required<EmbedMedia>> = [];
	const seenMediaKeys = new Set<string>();
	const tryAddMedia = (media?: Required<EmbedMedia>) => {
		if (!media || images.length >= MAX_GALLERY_MEDIA) return;
		const mediaKey = media.content_hash ?? media.url;
		if (seenMediaKeys.has(mediaKey)) return;
		seenMediaKeys.add(mediaKey);
		images.push(media);
	};
	const collectMedia = (candidate?: MessageEmbed) => {
		if (!candidate) return;
		if (normalizeUrl(candidate.url) !== normalizedUrl) return;
		const candidateMedia = isValidMedia(candidate.image)
			? candidate.image
			: isValidMedia(candidate.thumbnail)
				? candidate.thumbnail
				: undefined;
		tryAddMedia(candidateMedia);
	};
	for (let i = embedIndex; i < embedList.length && images.length < MAX_GALLERY_MEDIA; i++) {
		const candidate = embedList[i];
		collectMedia(candidate);
	}
	return images;
};
const getBorderColor = (color: number | undefined) => {
	if (color === undefined || color === 0) {
		return 'var(--brand-primary)';
	}
	return ColorUtils.int2rgb(color);
};

const mediaFocusRingClass = messageStyles.mediaFocusRing;
const LinkComponent: FC<LinkComponentProps> = observer(({url, children, className}) => {
	const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
		e.stopPropagation();
		try {
			const parsed = new URL(url);
			if (!TrustedDomain.isTrustedDomain(parsed.hostname)) {
				e.preventDefault();
				openExternalUrlWithWarning(url);
			}
		} catch (_error) {
			logger.warn('Invalid URL in embed link:', url);
		}
	};
	return (
		<FocusRing>
			<a
				className={clsx(styles.embedLink, className)}
				href={url}
				rel="noopener noreferrer"
				target="_blank"
				onClick={handleClick}
			>
				{children}
			</a>
		</FocusRing>
	);
});
const EmbedProvider: FC<{provider?: EmbedAuthor}> = observer(({provider}) => {
	if (!provider) return null;
	return (
		<div className={styles.embedProvider}>
			{provider.url ? <LinkComponent url={provider.url}>{provider.name}</LinkComponent> : <span>{provider.name}</span>}
		</div>
	);
});
const EmbedAuthorComponent: FC<{author?: EmbedAuthor}> = observer(({author}) => {
	if (!author) return null;
	return (
		<div className={styles.embedAuthor}>
			{author.proxy_icon_url && (
				<img
					alt=""
					className={styles.embedAuthorIcon}
					src={buildMediaProxyURL(author.proxy_icon_url)}
					width={24}
					height={24}
				/>
			)}
			{author.url ? (
				<LinkComponent className={clsx(styles.embedAuthorName, styles.embedAuthorNameLink)} url={author.url}>
					{author.name}
				</LinkComponent>
			) : (
				<span className={styles.embedAuthorName}>{author.name}</span>
			)}
		</div>
	);
});
const EmbedTitle: FC<{title?: string; url?: string}> = observer(({title, url}) => {
	if (title == null || title.length === 0) return null;
	return (
		<div className={styles.embedTitle}>
			{url ? (
				<LinkComponent url={url}>
					<SafeMarkdown content={title} options={{context: MarkdownContext.RESTRICTED_INLINE_REPLY}} />
				</LinkComponent>
			) : (
				<span>
					<SafeMarkdown content={title} options={{context: MarkdownContext.RESTRICTED_INLINE_REPLY}} />
				</span>
			)}
		</div>
	);
});
const EmbedDescription: FC<{
	messageId?: string;
	channelId?: string;
	description?: string;
}> = observer(({messageId, channelId, description}) => {
	if (!description) return null;
	return (
		<div className={styles.embedDescription}>
			<SafeMarkdown
				content={description}
				options={{
					context: MarkdownContext.RESTRICTED_EMBED_DESCRIPTION,
					messageId,
					channelId,
				}}
			/>
		</div>
	);
});
const EmbedFields: FC<{fields: Array<EmbedField>}> = observer(({fields}) => {
	if (!fields?.length) return null;
	const groupFields = (fields: Array<EmbedField>): Array<Array<EmbedField>> => {
		const groupedFields: Array<Array<EmbedField>> = [];
		let currentGroup: Array<EmbedField> = [];
		const MAX_INLINE_PER_ROW = 3;
		for (const field of fields) {
			if (field.inline) {
				currentGroup.push(field);
				if (currentGroup.length === MAX_INLINE_PER_ROW) {
					groupedFields.push(currentGroup);
					currentGroup = [];
				}
			} else {
				if (currentGroup.length > 0) {
					groupedFields.push(currentGroup);
					currentGroup = [];
				}
				groupedFields.push([field]);
			}
		}
		if (currentGroup.length > 0) {
			groupedFields.push(currentGroup);
		}
		return groupedFields;
	};
	const groupedFields = groupFields(fields);
	return (
		<div className={styles.embedFields}>
			{groupedFields.map((group, groupIndex) => {
				const groupLength = group.length;
				return group.map(({name, value}, index) => {
					const span = groupLength === 1 ? 12 : groupLength === 2 ? 6 : 4;
					const gridColumnStart = index * span + 1;
					const gridColumnEnd = gridColumnStart + span;
					return (
						<div
							className={styles.embedField}
							key={`${groupIndex}-${index}`}
							style={{gridColumn: `${gridColumnStart} / ${gridColumnEnd}`}}
						>
							<div className={styles.embedFieldName}>
								<SafeMarkdown content={name} options={{context: MarkdownContext.RESTRICTED_INLINE_REPLY}} />
							</div>
							<div className={styles.embedFieldValue}>
								<SafeMarkdown content={value} options={{context: MarkdownContext.RESTRICTED_EMBED_DESCRIPTION}} />
							</div>
						</div>
					);
				});
			})}
		</div>
	);
});
const EmbedFooterComponent: FC<{
	timestamp?: Date;
	footer?: EmbedFooter;
}> = observer(({timestamp, footer}) => {
	const {i18n} = useLingui();
	const formattedTimestamp = timestamp ? DateUtils.getRelativeDateString(timestamp, i18n) : undefined;
	if (!(footer || formattedTimestamp)) return null;
	return (
		<div className={clsx(styles.embedFooter, footer?.proxy_icon_url && styles.hasThumbnail)}>
			{footer?.proxy_icon_url && (
				<img
					alt=""
					className={styles.embedFooterIcon}
					src={buildMediaProxyURL(footer.proxy_icon_url)}
					width={20}
					height={20}
				/>
			)}
			<div className={styles.embedFooterText}>
				{footer?.text && (
					<SafeMarkdown content={footer.text} options={{context: MarkdownContext.RESTRICTED_INLINE_REPLY}} />
				)}
				{formattedTimestamp && (
					<>
						{footer?.text && (
							<>
								{'​'}
								<div className={styles.embedFooterSeparator} />
								{'​'}
							</>
						)}
						<span>{formattedTimestamp}</span>
					</>
				)}
			</div>
		</div>
	);
});
const EmbedMediaRenderer: FC<{
	embed: MessageEmbed;
	message: Message;
	embedIndex?: number;
	onDelete?: (bypassConfirm?: boolean) => void;
	isPreview?: boolean;
}> = observer(({embed, message, embedIndex, onDelete, isPreview}) => {
	const {video, image, thumbnail} = embed;
	if (!isValidMedia(video) && !isValidMedia(image) && !isValidMedia(thumbnail)) {
		return null;
	}
	if (isValidMedia(video) && embed.provider?.url && new URL(embed.provider.url).hostname === 'www.youtube.com') {
		return (
			<FocusRing within ringClassName={mediaFocusRingClass}>
				<EmbedYouTube embed={embed} />
			</FocusRing>
		);
	}
	if (isValidMedia(video)) {
		const {width, height} = calculateMediaDimensions(video);
		return (
			<FocusRing within ringClassName={mediaFocusRingClass}>
				<EmbedVideo
					src={buildMediaProxyURL(video.proxy_url)}
					width={width}
					height={height}
					placeholder={video.placeholder}
					title={embed.title}
					alt={video.description ?? undefined}
					duration={video.duration}
					nsfw={isMediaNSFW(video)}
					channelId={message.channelId}
					messageId={message.id}
					embedUrl={embed.url}
					message={message}
					contentHash={video.content_hash}
					embedIndex={embedIndex}
					onDelete={onDelete}
					isPreview={isPreview}
				/>
			</FocusRing>
		);
	}
	if (isValidMedia(image)) {
		const {width, height} = calculateMediaDimensions(image);
		const isGif = image.content_type === 'image/gif' || image.url.toLowerCase().endsWith('.gif');
		const imageIsAnimated = (image.flags & MessageAttachmentFlags.IS_ANIMATED) === MessageAttachmentFlags.IS_ANIMATED;
		if (isGif) {
			return (
				<FocusRing within ringClassName={mediaFocusRingClass}>
					<EmbedGif
						embedURL={image.url}
						proxyURL={image.proxy_url}
						naturalWidth={image.width}
						naturalHeight={image.height}
						placeholder={image.placeholder}
						alt={image.description ?? embed.description ?? undefined}
						nsfw={isMediaNSFW(image)}
						channelId={message.channelId}
						messageId={message.id}
						message={message}
						contentHash={image.content_hash}
						embedIndex={embedIndex}
						onDelete={onDelete}
						isPreview={isPreview}
					/>
				</FocusRing>
			);
		}
		return (
			<FocusRing within ringClassName={mediaFocusRingClass}>
				<EmbedImage
					src={getOptimizedMediaURL(image.proxy_url, width, height)}
					originalSrc={image.url}
					naturalWidth={image.width}
					naturalHeight={image.height}
					width={width}
					height={height}
					placeholder={image.placeholder}
					constrain={true}
					nsfw={isMediaNSFW(image)}
					channelId={message.channelId}
					messageId={message.id}
					message={message}
					contentHash={image.content_hash}
					embedIndex={embedIndex}
					onDelete={onDelete}
					isPreview={isPreview}
					animated={imageIsAnimated}
					alt={image.description ?? undefined}
				/>
			</FocusRing>
		);
	}
	if (isValidMedia(thumbnail)) {
		const {width, height} = calculateMediaDimensions(thumbnail);
		const isGif = thumbnail.content_type === 'image/gif' || thumbnail.url.toLowerCase().endsWith('.gif');
		const thumbnailIsAnimated =
			(thumbnail.flags & MessageAttachmentFlags.IS_ANIMATED) === MessageAttachmentFlags.IS_ANIMATED;
		if (isGif) {
			return (
				<FocusRing within ringClassName={mediaFocusRingClass}>
					<EmbedGif
						embedURL={thumbnail.url}
						proxyURL={thumbnail.proxy_url}
						naturalWidth={thumbnail.width}
						naturalHeight={thumbnail.height}
						placeholder={thumbnail.placeholder}
						alt={thumbnail.description ?? embed.description ?? undefined}
						nsfw={isMediaNSFW(thumbnail)}
						channelId={message.channelId}
						messageId={message.id}
						message={message}
						contentHash={thumbnail.content_hash}
						embedIndex={embedIndex}
						onDelete={onDelete}
					/>
				</FocusRing>
			);
		}
		return (
			<FocusRing within ringClassName={mediaFocusRingClass}>
				<EmbedImage
					src={getOptimizedMediaURL(thumbnail.proxy_url, width, height)}
					originalSrc={thumbnail.url}
					naturalWidth={thumbnail.width}
					naturalHeight={thumbnail.height}
					width={width}
					height={height}
					placeholder={thumbnail.placeholder}
					constrain={true}
					nsfw={isMediaNSFW(thumbnail)}
					channelId={message.channelId}
					messageId={message.id}
					message={message}
					contentHash={thumbnail.content_hash}
					embedIndex={embedIndex}
					onDelete={onDelete}
					animated={thumbnailIsAnimated}
					alt={thumbnail.description ?? undefined}
				/>
			</FocusRing>
		);
	}
	return null;
});
const RichEmbed: FC<EmbedProps> = observer(({embed, message, embedIndex, contextualEmbeds, onDelete, isPreview}) => {
	const embedList = contextualEmbeds ?? message.embeds;
	const hasVideo = isValidMedia(embed.video);
	const hasImage = isValidMedia(embed.image);
	const hasThumbnail = isValidMedia(embed.thumbnail);
	const hasAnyMedia = hasVideo || hasImage || hasThumbnail;
	const galleryImages = useMemo<Array<Required<EmbedMedia>>>(() => {
		return collectGalleryImages({embed, embedIndex, embedList});
	}, [embed, embedIndex, contextualEmbeds, message.embeds]);
	const showGallery = galleryImages.length > 1 || (!hasAnyMedia && galleryImages.length > 0);
	const galleryAttachments = useMemo(
		() => (showGallery ? buildGalleryAttachments(galleryImages, embed, embedIndex) : undefined),
		[galleryImages, embed, embedIndex, showGallery],
	);
	const shouldRenderMedia = hasAnyMedia || showGallery;
	const isRichType = embed.type === MessageEmbedTypes.RICH;
	const isInlineThumbnail =
		!hasVideo && hasThumbnail && !hasImage && (isRichType || shouldRenderAsInlineThumbnail(embed.thumbnail));
	const shouldRenderInlineThumbnail = isInlineThumbnail && !showGallery;
	const isYouTubeEmbed = embed.provider?.url && new URL(embed.provider.url).hostname === 'www.youtube.com';
	const useNarrowWidth = shouldRenderMedia && !shouldRenderInlineThumbnail;
	return (
		<article
			className={clsx(styles.embed, styles.embedFull, markupStyles.markup)}
			data-search-highlight-scope="message"
			style={{
				borderLeft: `4px solid ${getBorderColor(embed.color)}`,
				maxWidth: useNarrowWidth ? 'min(100%, 432px)' : 'min(100%, 516px)',
			}}
		>
			<div className={styles.gridContainer}>
				<div className={clsx(styles.grid, shouldRenderInlineThumbnail && styles.hasThumbnail)}>
					<div className={styles.embedContent}>
						<EmbedProvider provider={embed.provider} />
						<EmbedAuthorComponent author={embed.author} />
						<EmbedTitle title={embed.title} url={embed.url} />
						{!isYouTubeEmbed && (
							<EmbedDescription description={embed.description} messageId={message.id} channelId={message.channelId} />
						)}
						<EmbedFields fields={embed.fields?.slice() ?? []} />
						{!shouldRenderInlineThumbnail && shouldRenderMedia && (
							<div className={clsx(styles.embedMedia)}>
								{showGallery && galleryAttachments ? (
									<AttachmentMosaic
										attachments={galleryAttachments}
										message={message}
										hideExpiryFootnote={true}
										isPreview={isPreview}
									/>
								) : (
									<EmbedMediaRenderer
										embed={embed}
										message={message}
										embedIndex={embedIndex}
										onDelete={onDelete}
										isPreview={isPreview}
									/>
								)}
							</div>
						)}
						{(embed.footer || embed.timestamp) && (
							<EmbedFooterComponent
								footer={embed.footer}
								timestamp={embed.timestamp ? new Date(embed.timestamp) : undefined}
							/>
						)}
					</div>
					{shouldRenderInlineThumbnail && embed.thumbnail && isValidMedia(embed.thumbnail) && (
						<div className={styles.embedThumbnail}>
							<FocusRing within ringClassName={mediaFocusRingClass}>
								<EmbedImage
									src={getOptimizedMediaURL(
										embed.thumbnail.proxy_url,
										Math.min(80, Math.round((80 * embed.thumbnail.width) / embed.thumbnail.height)),
										80,
										embed.thumbnail.content_type !== 'image/gif',
										embed.thumbnail.content_type === 'image/gif' ? true : undefined,
									)}
									originalSrc={embed.thumbnail.url}
									naturalWidth={embed.thumbnail.width}
									naturalHeight={embed.thumbnail.height}
									width={Math.min(80, Math.round((80 * embed.thumbnail.width) / embed.thumbnail.height))}
									height={80}
									placeholder={embed.thumbnail.placeholder}
									constrain={true}
									isInline={true}
									nsfw={isMediaNSFW(embed.thumbnail)}
									channelId={message.channelId}
									messageId={message.id}
									message={message}
									contentHash={embed.thumbnail.content_hash}
									embedIndex={embedIndex}
									onDelete={onDelete}
									isPreview={isPreview}
									alt={embed.thumbnail!.description ?? undefined}
								/>
							</FocusRing>
						</div>
					)}
				</div>
			</div>
		</article>
	);
});
const BlueskyEngagementRow: FC<{fields?: Array<EmbedField>}> = observer(({fields}) => {
	const {t} = useLingui();
	const isSingularCount = useCallback((count: string): boolean => {
		const normalised = count.replace(/[\s,]/g, '');
		if (normalised.length === 0) return false;
		const parsed = Number(normalised);
		return Number.isFinite(parsed) && parsed === 1;
	}, []);
	const getMetricLabel = useCallback(
		(metric: 'repost' | 'quote' | 'like' | 'save', count: string): string => {
			const singular = isSingularCount(count);
			switch (metric) {
				case 'repost':
					return singular ? t`repost` : t`reposts`;
				case 'quote':
					return singular ? t`quote` : t`quotes`;
				case 'like':
					return singular ? t`like` : t`likes`;
				case 'save':
					return singular ? t`save` : t`saves`;
			}
		},
		[isSingularCount, t],
	);
	const engagementItems = useMemo(() => {
		const map = new Map<string, string>();
		for (const field of fields ?? []) {
			map.set(field.name, field.value);
		}
		const shouldRenderCount = (count?: string): count is string => {
			if (!count) return false;
			const trimmed = count.trim();
			if (trimmed.length === 0) return false;
			return !/^0(?:\.0+)?$/.test(trimmed);
		};
		const items: Array<{metric: 'repost' | 'quote' | 'like' | 'save'; count: string}> = [];
		const repostCount = map.get('repostCount');
		const quoteCount = map.get('quoteCount');
		const likeCount = map.get('likeCount');
		const bookmarkCount = map.get('bookmarkCount') ?? map.get('saveCount');
		if (shouldRenderCount(repostCount)) items.push({metric: 'repost', count: repostCount});
		if (shouldRenderCount(quoteCount)) items.push({metric: 'quote', count: quoteCount});
		if (shouldRenderCount(likeCount)) items.push({metric: 'like', count: likeCount});
		if (shouldRenderCount(bookmarkCount)) items.push({metric: 'save', count: bookmarkCount});
		return items;
	}, [fields]);
	if (engagementItems.length === 0) return null;
	return (
		<div className={styles.blueskyEngagement}>
			{engagementItems.map(({metric, count}) => (
				<div key={metric} className={styles.blueskyEngagementItem}>
					<strong>{count}</strong> {getMetricLabel(metric, count)}
				</div>
			))}
		</div>
	);
});
interface BlueskyEmbedProps extends EmbedProps {
	isNested?: boolean;
}
const BlueskyEmbed: FC<BlueskyEmbedProps> = observer(
	({embed, message, embedIndex, contextualEmbeds, onDelete, isPreview, isNested = false}) => {
		const embedList = isNested ? [embed] : (contextualEmbeds ?? message.embeds);
		const hasVideo = isValidMedia(embed.video);
		const hasImage = isValidMedia(embed.image);
		const hasThumbnail = isValidMedia(embed.thumbnail);
		const hasAnyMedia = hasVideo || hasImage || hasThumbnail;
		const galleryImages = useMemo<Array<Required<EmbedMedia>>>(() => {
			return collectGalleryImages({embed, embedIndex, embedList});
		}, [embed, embedIndex, embedList]);
		const showGallery = galleryImages.length > 1 || (!hasAnyMedia && galleryImages.length > 0);
		const galleryAttachments = useMemo(
			() => (showGallery ? buildGalleryAttachments(galleryImages, embed, embedIndex) : undefined),
			[galleryImages, embed, embedIndex, showGallery],
		);
		const shouldRenderMedia = hasAnyMedia || showGallery;
		const blueskyMediaMaxWidth = useMemo(() => {
			if (showGallery || isNested) return undefined;
			const primaryMedia = hasVideo ? embed.video : hasImage ? embed.image : hasThumbnail ? embed.thumbnail : undefined;
			if (!primaryMedia || !isValidMedia(primaryMedia)) return undefined;
			return calculateMediaDimensions(primaryMedia).width;
		}, [embed.video, embed.image, embed.thumbnail, hasVideo, hasImage, hasThumbnail, isNested, showGallery]);
		const blueskyMediaContainerStyle = useMemo(() => {
			if (blueskyMediaMaxWidth === undefined) return undefined;
			return {width: `min(100%, ${blueskyMediaMaxWidth}px)`, maxWidth: `min(100%, ${blueskyMediaMaxWidth}px)`};
		}, [blueskyMediaMaxWidth]);
		const useNarrowWidth = shouldRenderMedia;
		const nestedChildEmbed = useMemo(() => {
			if (isNested) return undefined;
			const candidate = embed.children?.[0];
			if (!candidate) return undefined;
			if (candidate.type !== MessageEmbedTypes.BLUESKY) return undefined;
			return candidate;
		}, [embed.children, isNested]);
		const renderNestedChild = () => {
			if (!nestedChildEmbed) {
				return null;
			}
			return (
				<div className={styles.blueskyNestedEmbedContainer}>
					<BlueskyEmbed
						embed={nestedChildEmbed}
						message={message}
						onDelete={onDelete}
						isPreview={isPreview}
						isNested={true}
					/>
				</div>
			);
		};
		return (
			<article
				className={clsx(styles.embed, markupStyles.markup, isNested ? styles.blueskyNestedEmbed : styles.embedFull)}
				data-search-highlight-scope="message"
				style={{
					...(isNested
						? {
								maxWidth: '100%',
								width: '100%',
							}
						: {
								borderLeft: `4px solid ${getBorderColor(embed.color)}`,
								maxWidth:
									blueskyMediaMaxWidth !== undefined
										? `min(100%, ${blueskyMediaMaxWidth + EMBED_MEDIA_CHROME_WIDTH}px)`
										: useNarrowWidth
											? 'min(100%, 432px)'
											: 'min(100%, 516px)',
							}),
				}}
			>
				<div className={styles.gridContainer}>
					<div className={styles.grid}>
						<div className={styles.embedContent}>
							<EmbedProvider provider={embed.provider} />
							<EmbedAuthorComponent author={embed.author} />
							{!isNested && <EmbedTitle title={embed.title} url={embed.url} />}
							<EmbedDescription description={embed.description} messageId={message.id} channelId={message.channelId} />
							{shouldRenderMedia ? (
								<div
									className={clsx(
										styles.blueskyMediaEngagement,
										isNested && styles.blueskyNestedMediaEngagement,
										showGallery && styles.blueskyGalleryMedia,
									)}
									style={blueskyMediaContainerStyle}
								>
									<div className={clsx(styles.embedMedia)}>
										{showGallery && galleryAttachments ? (
											<AttachmentMosaic
												attachments={galleryAttachments}
												message={message}
												hideExpiryFootnote={true}
												isPreview={isPreview}
											/>
										) : (
											<EmbedMediaRenderer
												embed={embed}
												message={message}
												embedIndex={embedIndex}
												onDelete={onDelete}
												isPreview={isPreview}
											/>
										)}
									</div>
									{renderNestedChild()}
								</div>
							) : (
								renderNestedChild()
							)}
							{!isNested && (
								<EmbedFooterComponent
									footer={embed.footer}
									timestamp={embed.timestamp ? new Date(embed.timestamp) : undefined}
								/>
							)}
							{!isNested && <BlueskyEngagementRow fields={embed.fields?.slice()} />}
						</div>
					</div>
				</div>
			</article>
		);
	},
);
export const Embed: FC<EmbedProps> = observer(({embed, message, embedIndex, contextualEmbeds, onDelete, isPreview}) => {
	const {t} = useLingui();
	const {enabled: isMobile} = MobileLayout;
	const channel = Channels.getChannel(message.channelId);
	const embedList = contextualEmbeds ?? message.embeds;
	const normalizedEmbedUrl = useMemo(() => normalizeUrl(embed.url), [embed.url]);
	const isDuplicateEmbed = useMemo(() => {
		if (embedIndex === undefined || !normalizedEmbedUrl) return false;
		for (let i = 0; i < embedIndex; i++) {
			if (normalizeUrl(embedList[i]?.url) === normalizedEmbedUrl) {
				return true;
			}
		}
		return false;
	}, [embedIndex, contextualEmbeds, message.embeds, normalizedEmbedUrl]);
	const canSuppressEmbeds = useCallback(() => {
		const guild = channel?.guildId ? Guilds.getGuild(channel.guildId) : null;
		const sendMessageDisabled = guild ? (guild.disabledOperations & GuildOperations.SEND_MESSAGE) !== 0 : false;
		if (sendMessageDisabled) return false;
		if (message.isCurrentUserAuthor()) return true;
		if (!channel || channel.isPrivate()) return false;
		return Permission.can(Permissions.MANAGE_MESSAGES, {channelId: message.channelId});
	}, [message, channel]);
	const handleSuppressEmbeds = useCallback(() => {
		ModalCommands.push(modal(() => <SuppressEmbedsConfirmModal message={message} />));
	}, [message]);
	const showSuppressButton = !isMobile && canSuppressEmbeds() && Accessibility.showSuppressEmbedsButton && !isPreview;
	const spoileredUrls = useMemo(() => extractSpoileredUrls(message.content), [message.content]);
	const {isSpoilerEmbed, matchingSpoilerUrls} = useMemo(() => {
		const urlsToCheck = [
			embed.url,
			embed.provider?.url,
			embed.image?.url,
			embed.image?.proxy_url,
			embed.thumbnail?.url,
			embed.thumbnail?.proxy_url,
			embed.video?.url,
			embed.video?.proxy_url,
			embed.audio?.url,
			embed.audio?.proxy_url,
		].filter(Boolean) as Array<string>;
		if (spoileredUrls.size === 0) return {isSpoilerEmbed: false, matchingSpoilerUrls: [] as Array<string>};
		const matches = urlsToCheck
			.map((url) => canonicalizeMediaUrl(url))
			.filter((canonical): canonical is string => !!canonical && spoileredUrls.has(canonical));
		return {isSpoilerEmbed: matches.length > 0, matchingSpoilerUrls: matches};
	}, [embed, message.content, spoileredUrls]);
	const {hidden: spoilerHidden, reveal: revealSpoiler} = useSpoilerState(
		isSpoilerEmbed,
		message.channelId,
		matchingSpoilerUrls,
	);
	const wrapSpoiler = (node: React.ReactElement) =>
		isSpoilerEmbed ? (
			<SpoilerOverlay hidden={spoilerHidden} onReveal={revealSpoiler}>
				{node}
			</SpoilerOverlay>
		) : (
			node
		);
	const renderSuppressButton = (extraClassName?: string) => {
		if (!showSuppressButton) {
			return null;
		}
		return (
			<button
				type="button"
				onClick={handleSuppressEmbeds}
				className={clsx(messageStyles.hoverAction, styles.suppressButton, extraClassName)}
				aria-label={t`Suppress Embeds`}
			>
				<XIcon size={16} weight="bold" />
			</button>
		);
	};
	const wrapMediaOnlyEmbed = (mediaContent: React.ReactNode) =>
		wrapSpoiler(
			<div className={styles.container} data-search-highlight-scope="message">
				<div className={styles.mediaFrame}>
					{renderSuppressButton(styles.mediaSuppressButton)}
					{mediaContent}
				</div>
			</div>,
		);
	if (isDuplicateEmbed) {
		return null;
	}
	const hasRichContent = hasRichEmbedContent(embed);
	if (!hasRichContent) {
		if (embed.type === MessageEmbedTypes.AUDIO && embed.audio?.proxy_url) {
			return wrapMediaOnlyEmbed(
				<FocusRing within ringClassName={mediaFocusRingClass}>
					<EmbedAudio
						src={embed.audio.proxy_url}
						title={embed.title}
						duration={embed.audio.duration}
						embedUrl={embed.url}
						channelId={message.channelId}
						messageId={message.id}
						message={message}
						contentHash={embed.audio.content_hash}
						embedIndex={embedIndex}
						onDelete={onDelete}
						isPreview={isPreview}
					/>
				</FocusRing>,
			);
		}
		if (embed.type === MessageEmbedTypes.VIDEO && isValidMedia(embed.video)) {
			if (embed.provider?.url && new URL(embed.provider.url).hostname === 'www.youtube.com') {
				return wrapMediaOnlyEmbed(
					<FocusRing within ringClassName={mediaFocusRingClass}>
						<EmbedYouTube embed={embed} />
					</FocusRing>,
				);
			}
			const {width, height} = calculateMediaDimensions(embed.video);
			return wrapMediaOnlyEmbed(
				<FocusRing within ringClassName={mediaFocusRingClass}>
					<EmbedVideo
						src={embed.video.proxy_url}
						width={width}
						height={height}
						placeholder={embed.video.placeholder}
						title={embed.title}
						alt={embed.video.description ?? undefined}
						duration={embed.video.duration}
						nsfw={isMediaNSFW(embed.video)}
						channelId={message.channelId}
						messageId={message.id}
						embedUrl={embed.url}
						message={message}
						contentHash={embed.video.content_hash}
						embedIndex={embedIndex}
						onDelete={onDelete}
					/>
				</FocusRing>,
			);
		}
		const {thumbnail} = embed;
		if (
			embed.type === MessageEmbedTypes.IMAGE &&
			isValidMedia(thumbnail) &&
			(thumbnail.flags & MessageAttachmentFlags.IS_ANIMATED) === MessageAttachmentFlags.IS_ANIMATED
		) {
			const {width, height} = calculateMediaDimensions(thumbnail);
			return wrapMediaOnlyEmbed(
				<FocusRing within ringClassName={mediaFocusRingClass}>
					<EmbedGif
						embedURL={thumbnail.url}
						proxyURL={getOptimizedMediaURL(thumbnail.proxy_url, width, height)}
						naturalWidth={thumbnail.width}
						naturalHeight={thumbnail.height}
						placeholder={thumbnail.placeholder}
						alt={thumbnail.description ?? embed.description ?? undefined}
						channelId={message.channelId}
						messageId={message.id}
						message={message}
						contentHash={thumbnail.content_hash}
						embedIndex={embedIndex}
						onDelete={onDelete}
					/>
				</FocusRing>,
			);
		}
		if (embed.type === MessageEmbedTypes.GIFV && isValidMedia(embed.video) && isValidMedia(thumbnail) && embed.url) {
			return wrapMediaOnlyEmbed(
				<FocusRing within ringClassName={mediaFocusRingClass}>
					<EmbedGifv
						embedURL={embed.url}
						videoProxyURL={embed.video.proxy_url}
						videoURL={embed.video.url}
						naturalWidth={thumbnail.width}
						naturalHeight={thumbnail.height}
						placeholder={thumbnail.placeholder}
						alt={embed.video.description ?? thumbnail.description ?? embed.description ?? undefined}
						channelId={message.channelId}
						messageId={message.id}
						message={message}
						contentHash={embed.video.content_hash}
						embedIndex={embedIndex}
						onDelete={onDelete}
						providerName={embed.provider?.name}
					/>
				</FocusRing>,
			);
		}
		if (isValidMedia(thumbnail)) {
			const {width, height} = calculateMediaDimensions(thumbnail);
			const isGif = thumbnail.content_type === 'image/gif' || thumbnail.url.toLowerCase().endsWith('.gif');
			if (isGif) {
				return wrapMediaOnlyEmbed(
					<FocusRing within ringClassName={mediaFocusRingClass}>
						<EmbedGif
							embedURL={thumbnail.url}
							proxyURL={thumbnail.proxy_url}
							naturalWidth={thumbnail.width}
							naturalHeight={thumbnail.height}
							placeholder={thumbnail.placeholder}
							alt={thumbnail.description ?? embed.description ?? undefined}
							nsfw={isMediaNSFW(thumbnail)}
							channelId={message.channelId}
							messageId={message.id}
							message={message}
							contentHash={thumbnail.content_hash}
							embedIndex={embedIndex}
							onDelete={onDelete}
						/>
					</FocusRing>,
				);
			}
			return wrapMediaOnlyEmbed(
				<FocusRing within ringClassName={mediaFocusRingClass}>
					<EmbedImage
						src={getOptimizedMediaURL(thumbnail.proxy_url, width, height)}
						originalSrc={thumbnail.url}
						naturalWidth={thumbnail.width}
						naturalHeight={thumbnail.height}
						width={width}
						height={height}
						placeholder={thumbnail.placeholder}
						constrain={true}
						nsfw={isMediaNSFW(thumbnail)}
						channelId={message.channelId}
						messageId={message.id}
						message={message}
						contentHash={thumbnail.content_hash}
						embedIndex={embedIndex}
						onDelete={onDelete}
					/>
				</FocusRing>,
			);
		}
		return null;
	}
	return wrapSpoiler(
		<div className={styles.container} data-search-highlight-scope="message">
			{showSuppressButton && (
				<button
					type="button"
					onClick={handleSuppressEmbeds}
					className={clsx(messageStyles.hoverAction, styles.suppressButton)}
					aria-label={t`Suppress Embeds`}
				>
					<XIcon size={16} weight="bold" />
				</button>
			)}
			{embed.type === MessageEmbedTypes.BLUESKY ? (
				<BlueskyEmbed
					embed={embed}
					message={message}
					embedIndex={embedIndex}
					contextualEmbeds={contextualEmbeds}
					onDelete={onDelete}
					isPreview={isPreview}
				/>
			) : (
				<RichEmbed
					embed={embed}
					message={message}
					embedIndex={embedIndex}
					contextualEmbeds={contextualEmbeds}
					onDelete={onDelete}
					isPreview={isPreview}
				/>
			)}
		</div>,
	);
});
